import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {firstValueFrom, Observable, Subject} from "rxjs";
import {environment} from "src/environments/environment";
import {Person, SessionContext} from "../models/session-context.model";
import {Router} from "@angular/router";
import {take} from "rxjs/operators";
import {UserInfoService} from "./user-info.service";
import {ModalService} from "./modal.service";
import {AuthService} from "./auth.service";
import {UserInfo} from "../models/user-info.model";
import {NgxSpinnerService} from "ngx-spinner";
import {TranslateService} from "@ngx-translate/core";
import { ErrorCodes } from "../shared/constants/error-codes.enum";

@Injectable({
  providedIn: "root",
})
export class ContactInfoService {
  url: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/general-request/";

  termsUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/general-request/accept-terms";

  termsServStatus = new Subject<number>();

  personId: number;

  userInfo: UserInfo = {
    userEmail: "",
    userPhone: "",
  };

  contactData: ContactObj;
  termsData: TermsObj;
  fetchContactData: FetchObj;

  userProfileInfo: UserInfo = {
    userEmail: "",
    userPhone: "",
    isEmailConsent: false,
    emailId: null,
    phoneId: null,
    isEmailValidated: false,
  };

  constructor(
    private http: HttpClient,
    private userInfoService: UserInfoService,
    private modalService: ModalService,
    private authService: AuthService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
  ) {
  }

  fetchValidTermsOfService(valid: boolean, termsContentType: string) {
    let fetchTermsObj = {
      acknowledgment: null,
      filter: {
        type: "filter",
        element: [
          {
            type: "field",
            name: "Terms",
          },
          {
            type: "field",
            name: "Terms.valid",
            value: [valid],
          },
          {
            type: "field",
            name: "Terms.termsContent",
            value: [termsContentType],
          },
        ],
      },
      mode: "FETCH",
    };

    return this.http
      .post<TermsBody>(this.url, fetchTermsObj, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise();
  }

  get sessionContext(): SessionContext {
    return JSON.parse(sessionStorage.getItem("sessionContext"));
  }

  get typeIdPhone() {
    return JSON.parse(sessionStorage.getItem("CONTACT_TYPE_PHONE"));
  }

  get typeIdEmail() {
    return JSON.parse(sessionStorage.getItem("CONTACT_TYPE_E_MAIL"));
  }

  get principalId() {
    return this.sessionContext.principal.id;
  }

  getContactInfo() {
    if (this.sessionContext !== null && this.sessionContext.isAuthorizedUser === true) {
      this.spinner.show();
      this.fetchContactInfo(this.principalId, this.typeIdEmail, this.typeIdPhone)
        .then((response) => {
          const responseCode = response.body.acknowledgment.responseCode;
          if (response.status === 200 && responseCode === "ACC") {
            this.spinner.hide();
            response.body.items.forEach((item) => {
              if (item.typeId === this.typeIdEmail) {
                this.userProfileInfo.personId = item.personId;
                this.userProfileInfo.userEmail = item.text;
                this.userProfileInfo.isEmailConsent = item.acceptNotifications;
                this.userProfileInfo.emailId = item.id;
                this.userProfileInfo.isEmailValidated = item.isEmailValidated;
              }
              if (item.typeId === this.typeIdPhone) {
                this.userProfileInfo.userPhone = item.text;
                this.userProfileInfo.phoneId = item.id;
              }
            });
            this.userInfoService.userInfo.next(this.userProfileInfo);
            sessionStorage.setItem("userInfo", JSON.stringify(this.userProfileInfo),);
          } else if (
            response.status === 200 &&
            (responseCode === "ERR" || responseCode === "REJ")
          ) {
            const messageCode = response.body.acknowledgment.ackonwledgmentDetailList ?
              response.body.acknowledgment.ackonwledgmentDetailList[0].messageCode : this.translate.instant('errorBackup');
            this.modalService.showErrorModal(messageCode, ErrorCodes.ERR028, true);
          }
          this.spinner.hide();
        })
        .catch((err) => {
          this.modalService.showErrorModal(err.error, ErrorCodes.ERR029, true);
        });
    }
  }

  async acceptTermsOfService(
    mode: string,
    personId: number,
    accepted: boolean,
    termsId: number,
    termsTextId?: number,
    id?: number,
    consentReference?: string,
  ) {
    this.termsData = {
      mode: mode,
      items: [
        {
          type: "termsOfService",
          id: id,
          personId: personId,
          accepted: accepted,
          termsId: termsId,
          termsTextId: termsTextId,
          consentReference: consentReference,
        },
      ],
    };
    try {
      const response = await firstValueFrom(
        this.http.post<SessionContext>(this.termsUrl, this.termsData, {
        withCredentials: true,
        observe: "response",
      })
      );
      console.log("TERMS ACC RESP!", response.body);
      if (accepted === false) {
        this.termsServStatus.next(0);
      }
      if (response) {
        if (response.status === 200 && response.body) {
          this.userInfoService.sessionContext.next(response.body);
          sessionStorage.setItem("sessionContext", JSON.stringify(response.body));
          this.termsServStatus.next(response.status);
        }
      } else {
        this.modalService.showErrorModal("SYS_0001", ErrorCodes.ERR031, true);
      }
    } catch
      (err) {
      this.modalService.showErrorModal(err.error.message, ErrorCodes.ERR032, true);
    }
  }

  async fetchContactInfo(personId: number, typeIdEmail, typeIdPhone) {
    this.fetchContactData = {
      filter: {
        type: "filter",
        element: [
          {
            type: "field",
            name: "Contact.personId",
            value: [personId],
          },
          {
            type: "field",
            name: "Contact.typeId",
            value: [typeIdEmail, typeIdPhone],
          },
        ],
      },
      mode: "FETCH",
    };

    return await this.http
      .post<FetchContactBody>(this.url, this.fetchContactData, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise();
  }

  async changeContactInfo(
    personId: number,
    email?: string,
    phone?: string,
    acceptNotifications?: boolean,
    emailChanged?: boolean
  ): Promise<number> {
    this.contactData = {
      acknowledgment: null,
      mode: "SAVE_PARTIAL",
      items: [
        {
          id: null,
          type: "contact",
          typeId: JSON.parse(sessionStorage.getItem("CONTACT_TYPE_E_MAIL")),
          text: email,
          personId: personId,
          acceptNotifications: acceptNotifications,
          acceptNotificationsTime: new Date(),
          isEmailValidated: emailChanged ? false : undefined,
        },
        {
          id: null,
          type: "contact",
          typeId: JSON.parse(sessionStorage.getItem("CONTACT_TYPE_PHONE")),
          text: phone,
          personId: personId,
        },
      ],
    };

    try {
      const response = await firstValueFrom(this.http
        .post<ContactObj>(this.url, this.contactData, {
          withCredentials: true,
          observe: "response",
        }),
      );

      if (response && response.status === 200) {
        const responseCode = response.body?.acknowledgment?.responseCode;
        if (responseCode === "ACC") {
          await firstValueFrom(this.modalService.showModal({
            isSuccess: true,
            showErrorReport: false,
            title: "settings.dataSuccessfullyUpdated",
          })
            .afterClosed());
          return response.status;
        } else if (responseCode === "ERR" || responseCode === "REJ") {
          this.modalService.showErrorModal(response.body.acknowledgment.acknowledgmentDetailList[0].messageCode, ErrorCodes.ERR033, true);
          return response.status;
        } else {
          this.modalService.showErrorModal("settings.dataUpdateFailed", ErrorCodes.ERR034, true);
          return response.status;
        }
      } else {
        this.modalService.showErrorModal("settings.dataUpdateFailed", ErrorCodes.ERR035, true);
        return response?.status || 500;
      }
    } catch (error) {
      this.modalService.showErrorModal("SYS_0001", ErrorCodes.ERR036, true);
      return 500;
    }
  }

  getTermsServStatus(): Observable<number> {
    return this.termsServStatus.asObservable();
  }
}

export interface Type {
  classificatorSystemId: number;
  code: string;
  id: number;
  insertTime: string;
  name: string;
  type: string;
}

export interface ContactItem {
  id: number;
  acceptNotifications: boolean;
  insertTime: string;
  person: Person;
  personId: number;
  text: string;
  type: Type;
  typeId: number;
  isEmailValidated: boolean;
}

export interface FetchContactBody {
  acknowledgment?: any;
  filter: string[];
  items: ContactItem[];
  mode: string;
  pagination: number[];
}

export interface TermsObj {
  acknowledgment?: any;
  mode: string;
  items: [
    {
      type: string;
      id: number;
      personId: number;
      accepted: boolean;
      updateTime?: Date;
      termsId: number;
      termsTextId: number;
      consentReference: string
    }
  ];
}

export interface TermsBody {
  acknowledgment: any;
  mode: string;
  items: [TermsText];
}

export interface TermsText {
  id: number;
  termsId: number;
  text: string;
  insertTime: Date;
  updateTime: Date;
  lang: string;
  accepted?: boolean;
}

export interface ContactObj {
  acknowledgment?: any;
  mode: string;
  items: [
    {
      id?: number;
      type?: string;
      typeId?: number;
      text?: string;
      personId?: number;
      acceptNotifications?: boolean;
      acceptNotificationsTime?: Date;
      isEmailValidated?: boolean;
    },
    {
      id?: number;
      type?: string;
      typeId?: number;
      text?: string;
      personId?: number;
      isEmailValidated?: boolean;
    }
  ];
}

export interface FetchObj {
  acknowledgment?: any;
  filter: {
    type: string;
    element: [
      { type: string; name: string; value?: any[] },
      { type: string; name: string; value?: any[] }
    ];
  };
  mode: string;
}
